import React, { Suspense, useEffect, useState } from 'react';
import { BrowserRouter as Router, Route, Switch, Redirect, useLocation } from 'react-router-dom';
import { ParallaxProvider } from 'react-scroll-parallax';
import { useDispatch, useSelector } from 'react-redux';
import { checkAuth, setUser } from './redux/slices/authSlice';

import { useGetCurrentUserQuery } from './redux/apis/endpoints/authApi';
import { useGetLaunchConfigQuery } from './redux/apis/endpoints/launchConfigApi';
import { setLaunchConfig } from './redux/slices/launchConfigSlice';
import { Toaster } from 'react-hot-toast';
import SubscriptionManager from './components/credits/SubscriptionManager';
import { detectUserCountry } from './redux/slices/locationSlice';
import { Capacitor } from '@capacitor/core';

// Core components (always loaded)
import Nav from './components/nav/Nav';
import BottomNav from './components/nav/BottomNav';
import Footer from './components/footer/Footer';
import PrivateRoute from './routes/PrivateRoute';
import PublicRoute from './routes/PublicRoute';
import ScrollToTop from './ScrollToTop';
import StripeVerifiedRoute from './components/stripe/StripeVerifiedRoute';
import LoadingSpinner from './components/common/LoadingSpinner';

// Eagerly loaded pages (high-traffic, first-paint critical)
import LandingPage from './pages/landing/LandingPage';
import BrowsePage from './pages/browse/BrowsePage';
import ProductPage from './pages/product/ProductPage';

// Lazy loaded pages (loaded only when route is visited)
const HomePage = React.lazy(() => import('./pages/home/HomePage'));
const AboutPage = React.lazy(() => import('./pages/about/AboutPage'));
const FeaturesPage = React.lazy(() => import('./pages/features/FeaturesPage'));
const PrivacyPolicyPage = React.lazy(() => import('./pages/legal/PrivacyPolicyPage'));
const TermsOfServicePage = React.lazy(() => import('./pages/legal/TermsOfServicePage'));
const ContactPage = React.lazy(() => import('./pages/contact/ContactPage'));
const SearchPage = React.lazy(() => import('./pages/SearchPage'));

// Auth pages (lazy - only needed for unauthenticated users)
const Login = React.lazy(() => import('./pages/auth/Login'));
const Register = React.lazy(() => import('./pages/auth/Register'));
const ForgotPassword = React.lazy(() => import('./pages/auth/ForgotPassword'));
const ResetPassword = React.lazy(() => import('./pages/auth/ResetPassword'));
const EmailVerification = React.lazy(() => import('./pages/auth/EmailVerification'));
const CheckEmail = React.lazy(() => import('./pages/auth/CheckEmail'));
const CustomEmailVerification = React.lazy(() => import('./pages/auth/CustomEmailVerification'));
const PhoneVerification = React.lazy(() => import('./pages/auth/PhoneVerification'));
const SocialCallback = React.lazy(() => import('./pages/auth/SocialCallback'));
const ClaimAccount = React.lazy(() => import('./pages/auth/ClaimAccount'));

// Heavy feature pages (lazy - contain mapbox, VTO, etc.)
const ClosetPage = React.lazy(() => import('./pages/closet/ClosetPage'));
const MapPage = React.lazy(() => import('./pages/map/MapPage'));

// Dashboard & merchant (lazy - authenticated-only, many sub-components)
const Dashboard = React.lazy(() => import('./pages/dashboard/DashboardPage'));
const MerchantPage = React.lazy(() => import('./pages/merchant/MerchantPage'));
const MessagePage = React.lazy(() => import('./pages/messages/MessagePage'));
const FulfillmentGroupDetailPage = React.lazy(() => import('./pages/dashboard/FulfillmentGroupDetailPage'));
const CustomerReviewPage = React.lazy(() => import('./pages/dashboard/components/CustomerReviewPage'));
const ReviewsPage = React.lazy(() => import('./pages/review/ReviewsPage'));

// Checkout & cart (lazy - only needed during purchase flow)
const Cart = React.lazy(() => import('./pages/cart/CartPage'));
const CheckoutPage = React.lazy(() => import('./pages/checkout/CheckoutPage'));
const ConfirmationPage = React.lazy(() => import('./pages/checkout/ConfirmationPage'));

// Merchant tools (lazy - only merchants)
const ListingForm = React.lazy(() => import('./components/listings/ListingForm'));
const Favorites = React.lazy(() => import('./components/favorites/Favorites'));
const MerchantOnboarding = React.lazy(() => import('./components/stripe/MerchantOnboarding'));
const ShippingStatusPage = React.lazy(() => import('./pages/merchant/ship/ShippingStatusPage'));

// Review & return pages (lazy - rare usage)
const ProductReviewPage = React.lazy(() => import('./pages/product/components/ProductReviewPage'));
const ReturnRequestPage = React.lazy(() => import('./pages/dashboard/returns/ReturnRequestPage'));
const RequestReturnPage = React.lazy(() => import('./pages/dashboard/returns/RequestReturnPage'));
const ProcessReturnPage = React.lazy(() => import('./pages/dashboard/returns/ProcessReturnPage'));
const ProductConditionUpdatePage = React.lazy(() => import('./pages/dashboard/returns/ProductConditionUpdatePage'));
const ReturnShippingPage = React.lazy(() => import('./pages/dashboard/returns/ReturnShippingPage'));
const ReturnLabelSuccessPage = React.lazy(() => import('./pages/dashboard/returns/ReturnLabelSuccessPage'));
const KeepItemsPage = React.lazy(() => import('./pages/dashboard/returns/KeepItemsPage'));
const RequestKeepItemsPage = React.lazy(() => import('./pages/dashboard/returns/RequestKeepItemsPage'));
const ProcessKeepItemsPage = React.lazy(() => import('./pages/dashboard/returns/ProcessKeepItemsPage'));
const ProcessMaintenanceFeePage = React.lazy(() => import('./pages/dashboard/returns/ProcessMaintenanceFeePage'));

// Inner component that has access to Router context
const AppContent = () => {
  const dispatch = useDispatch();
  const authState = useSelector(state => state.auth);

  // Fetch launch config
  const { data: launchConfig } = useGetLaunchConfigQuery();

  // Sync launch config to Redux store
  useEffect(() => {
    if (launchConfig) {
      dispatch(setLaunchConfig(launchConfig));
    }
  }, [launchConfig, dispatch]);

  // Fetch user data if authenticated but no user data in Redux
  const { data: userData } = useGetCurrentUserQuery(undefined, {
    skip: !authState.isAuthenticated || !!authState.user,
    refetchOnMountOrArgChange: false,
  });

  useEffect(() => {
    dispatch(checkAuth());
    dispatch(detectUserCountry());
  }, [dispatch]);

  // Update Redux with fresh user data when fetched
  useEffect(() => {
    if (userData && authState.isAuthenticated && !authState.user) {
      dispatch(setUser(userData));
    }
  }, [userData, authState.isAuthenticated, authState.user, dispatch]);

  const location = useLocation();
  const isFullscreenRoute = ['/closet', '/map'].includes(location.pathname);

  const [isNativeApp, setIsNativeApp] = useState(false);

  useEffect(() => {
    const checkNative = () => {
      const isReactNative = !!window.ReactNativeWebView;
      const isCapacitor = Capacitor.isNativePlatform();
      const isCustomUA = navigator.userAgent.includes('SHEYOUApp');
      setIsNativeApp(isReactNative || isCapacitor || isCustomUA);
    };

    checkNative();

    // Fallback checking in case of race condition injecting ReactNativeWebView
    const timer = setTimeout(checkNative, 500);
    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      <ScrollToTop />
      <Toaster
        position="top-center"
        toastOptions={{
          style: {
            background: '#FFFFFF',
            color: '#000000',
          },
        }}
      />
      <div className="App flex flex-col min-h-screen">

        {!isNativeApp && <Nav />}
        {isNativeApp && (
          <div className="flex items-center justify-center p-3 bg-white border-b sticky top-0 z-50">
            <span className="font-bold text-xl" style={{ fontFamily: "'Space Mono', monospace", fontSize: 15, fontWeight: 700, letterSpacing: 5, color: '#1A1A1A' }}>SHEYOU</span>
          </div>
        )}
        <main className={`${isFullscreenRoute ? 'flex-grow overflow-hidden' : 'flex-grow'} ${isNativeApp ? 'pb-[env(safe-area-inset-bottom)] mb-16' : ''}`}>
          <Suspense fallback={<LoadingSpinner />}>
            <Switch>
              <Route exact path="/" component={LandingPage} />
              <Route exact path="/home-old" component={HomePage} />
              <Route path="/browse" component={BrowsePage} />
              <Route path="/about" component={AboutPage} />
              <Route path="/features" component={FeaturesPage} />
              <Route path="/contact" component={ContactPage} />
              <Route path="/privacy-policy" component={PrivacyPolicyPage} />
              <Route path="/terms-of-service" component={TermsOfServicePage} />
              <PublicRoute path="/login" component={Login} restricted={true} />
              <PublicRoute path="/register" component={Register} restricted={true} />
              <PublicRoute path="/signup" component={Register} restricted={true} />

              {/* Review routes first - more specific paths should be before generic paths */}
              <PrivateRoute
                path="/fulfillmentgroup/:fulfillmentGroupId/product/:productId/review"
                component={ProductReviewPage}
              />
              <PrivateRoute
                path="/fulfillmentgroup/:fulfillmentGroupId/review-products"
                component={ProductReviewPage}
              />

              {/* Generic product and merchant routes */}
              <Route path="/product/:id" component={ProductPage} />
              <Route path="/merchant/:id" component={MerchantPage} exact />
              <Route path="/search" component={SearchPage} />
              <Route path="/closet" component={ClosetPage} />
              <Route path="/map" component={MapPage} />

              {/* Auth Routes */}
              <PublicRoute path="/forgot-password" component={ForgotPassword} restricted={true} />
              <PublicRoute path="/auth/forgot-password" component={ForgotPassword} restricted={true} />
              <PublicRoute path="/reset-password" component={ResetPassword} restricted={false} />
              <PublicRoute path="/auth/reset-password" component={ResetPassword} restricted={false} />
              <PublicRoute path="/auth/custom-reset-password" component={ResetPassword} restricted={false} />
              <PublicRoute path="/auth/confirm" component={EmailVerification} restricted={false} />
              <PublicRoute path="/auth/custom-confirm" component={CustomEmailVerification} restricted={false} />
              <PublicRoute path="/auth/check-email" component={CheckEmail} restricted={false} />
              <PublicRoute path="/auth/verify-phone" component={PhoneVerification} restricted={false} />
              <PublicRoute path="/auth/callback" component={SocialCallback} restricted={false} />
              <PublicRoute path="/claim/:token" component={ClaimAccount} restricted={false} />

              {/* Fulfillment Group Detail - must be before /dashboard catch-all */}
              <PrivateRoute path="/dashboard/fulfillment-group/:fulfillmentGroupId" component={FulfillmentGroupDetailPage} exact />

              {/* Protected Routes */}
              <PrivateRoute path="/dashboard" component={Dashboard} />
              <PrivateRoute path="/reviews/user/:userId" component={ReviewsPage} />

              {/* Conditional Payment Routes - Only show if launched */}
              <PrivateRoute path="/cart" component={Cart} />
              <PrivateRoute exact path="/checkout" component={CheckoutPage} />
              <PrivateRoute path="/checkout/confirmation" component={ConfirmationPage} />
              {/* Message Routes - Order Matters: More specific first */}
              <PrivateRoute path="/message/order/:orderId" component={MessagePage} />
              <PrivateRoute path="/message/fg/:fulfillmentGroupId" component={MessagePage} />
              <PrivateRoute path="/message/fulfillment-group/:fulfillmentGroupId" component={MessagePage} />
              <PrivateRoute path="/message/product/:productId" component={MessagePage} />
              <PrivateRoute path="/message/user/:userId" component={MessagePage} />
              <PrivateRoute exact path="/message/:chatId?" component={MessagePage} />
              <PrivateRoute path="/favorites" component={Favorites} />
              <PrivateRoute path="/merchant/verify" component={MerchantOnboarding} />
              <PrivateRoute path="/merchant/ship/:fulfillmentGroupIds" component={ShippingStatusPage} exact />

              {/* Review and Return Routes */}
              <PrivateRoute path="/returns/initiate/:fulfillmentGroupId/:orderItemComponentId" component={ReturnRequestPage} />
              <PrivateRoute path="/dashboard/returns/request/:fulfillmentGroupId/subgroup/:subgroupId" component={RequestReturnPage} roles={['customer']} />
              <PrivateRoute path="/dashboard/returns/request/:fulfillmentGroupId" component={RequestReturnPage} roles={['customer']} />
              <PrivateRoute path="/dashboard/returns/request-keep-items/:fulfillmentGroupId/:subgroupId" component={RequestKeepItemsPage} roles={['customer']} />
              <PrivateRoute path="/dashboard/returns/keep-items/:fulfillmentGroupId/:subgroupId" component={KeepItemsPage} roles={['customer']} />
              <PrivateRoute path="/dashboard/returns/process-keep-items/:fulfillmentGroupId" component={ProcessKeepItemsPage} roles={['merchant']} />
              <PrivateRoute path="/dashboard/returns/process-fee/:fulfillmentGroupId" component={ProcessMaintenanceFeePage} roles={['customer']} />
              <PrivateRoute path="/dashboard/returns/process/:fulfillmentGroupId" component={ProcessReturnPage} roles={['merchant']} />
              <PrivateRoute path="/dashboard/returns/update-condition/:fulfillmentGroupId" component={ProductConditionUpdatePage} roles={['merchant']} />
              <PrivateRoute path="/dashboard/returns/shipping/:orderItemId" component={ReturnShippingPage} roles={['customer']} />
              <PrivateRoute path="/dashboard/returns/:orderItemId/success" component={ReturnLabelSuccessPage} roles={['customer']} />
              <PrivateRoute
                path="/fulfillmentgroup/:fulfillmentGroupId/customer/:customerId/review"
                component={CustomerReviewPage}
              />
              <PrivateRoute
                path="/order/:orderId/customer/:customerId/review"
                component={CustomerReviewPage}
              />
              <PrivateRoute path="/dashboard/customer-review/:customerId" component={CustomerReviewPage} />

              {/* Listing Routes */}
              <PrivateRoute exact path="/listing/upload" component={ListingForm} />
              <PrivateRoute path="/listing/edit/:id" component={ListingForm} />

              {/* Product Routes */}
              <Route path="/products" render={() => <Redirect to="/browse" />} />

              {/* 404 Route */}
              <Route path="*" render={() => <h1 className="text-center py-12 text-xl">Page not found</h1>} />
            </Switch>
          </Suspense>
        </main>
        {!isFullscreenRoute && !isNativeApp && <Footer />}
        {isNativeApp && <BottomNav />}
      </div>
    </>
  );
};

function App() {
  return (
    <ParallaxProvider>
      <Router>
        <SubscriptionManager />
        <AppContent />
      </Router>
    </ParallaxProvider>
  );
}

export default App; 